import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import ScreenLoading from 'components/ScreenLoading'
import InputField from 'components/InputField'
import api from 'containers/api'

interface PasswordRequirement {
  label: string
  met: boolean
}

const ChangePassword = () => {
  const token = useSelector(selectToken)
  const [showForm, setShowForm] = useState(false)
  const [step, setStep] = useState<'initial' | 'verify-email' | 'change-password'>('initial')

  // Form fields
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Verification
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationSent, setVerificationSent] = useState(false)

  // Status
  const [pendingRequest, setPendingRequest] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Password strength
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    { label: 'At least 8 characters', met: false },
    { label: 'Contains uppercase letter', met: false },
    { label: 'Contains lowercase letter', met: false },
    { label: 'Contains number', met: false },
    { label: 'Contains special character (!@#$%^&*)', met: false },
  ])

  useEffect(() => {
    if (newPassword) {
      setPasswordRequirements([
        { label: 'At least 8 characters', met: newPassword.length >= 8 },
        { label: 'Contains uppercase letter', met: /[A-Z]/.test(newPassword) },
        { label: 'Contains lowercase letter', met: /[a-z]/.test(newPassword) },
        { label: 'Contains number', met: /[0-9]/.test(newPassword) },
        { label: 'Contains special character (!@#$%^&*)', met: /[!@#$%^&*]/.test(newPassword) },
      ])
    }
  }, [newPassword])

  const passwordStrengthScore = passwordRequirements.filter(r => r.met).length
  const isPasswordValid = newPassword.length >= 8

  const getStrengthColor = () => {
    if (passwordStrengthScore <= 1) return '#dc3545'
    if (passwordStrengthScore <= 3) return '#ffc107'
    return '#28a745'
  }

  const getStrengthLabel = () => {
    if (passwordStrengthScore <= 1) return 'Weak'
    if (passwordStrengthScore <= 3) return 'Medium'
    return 'Strong'
  }

  const handleStartChange = () => {
    setShowForm(true)
    setStep('initial')
    setError('')
    setSuccess('')
  }

  const handleCancel = () => {
    setShowForm(false)
    setStep('initial')
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setVerificationCode('')
    setVerificationSent(false)
    setError('')
    setSuccess('')
  }

  const handleRequestVerification = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currentPassword) {
      setError('Please enter your current password')
      return
    }

    if (!isPasswordValid) {
      setError('New password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setPendingRequest(true)
    setError('')

    try {
      // Request email verification
      await api.post('/auth/password-change/request', {
        current_password: currentPassword,
        new_password: newPassword
      }, api.getHeaders(token), {})

      setVerificationSent(true)
      setStep('verify-email')
      setSuccess('Verification code sent to your email')
    } catch (err: any) {
      console.error('Error requesting verification:', err)
      if (err?.response?.data?.detail?.type === 'invalid_credentials') {
        setError('Current password is incorrect')
      } else {
        setError('Failed to send verification email. Please try again.')
      }
    } finally {
      setPendingRequest(false)
    }
  }

  const handleVerifyAndChange = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!verificationCode) {
      setError('Please enter the verification code')
      return
    }

    setPendingRequest(true)
    setError('')

    try {
      // Verify code and change password
      await api.post('/auth/password-change/verify', {
        verification_code: verificationCode,
        current_password: currentPassword,
        new_password: newPassword
      }, api.getHeaders(token), {})

      setSuccess('Password changed successfully!')
      setTimeout(() => {
        handleCancel()
      }, 2000)
    } catch (err: any) {
      console.error('Error changing password:', err)
      if (err?.response?.data?.detail?.type === 'invalid_code') {
        setError('Invalid or expired verification code')
      } else {
        setError('Failed to change password. Please try again.')
      }
    } finally {
      setPendingRequest(false)
    }
  }

  if (!showForm) {
    return (
      <div
        style={{
          padding: '1.5rem',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }} className="box-container">
          <span
            className="material-icons-outlined"
            style={{ fontSize: '2.5rem', color: '#495057' }}
          >
            lock
          </span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>Change Your Password</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#6c757d', lineHeight: '1.6' }}>
              Update your password to keep your account secure. We'll send a verification code to your email before making any changes.
            </p>
            <button
              type="button"
              className="submit-button"
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1.5rem',
                fontSize: '0.9rem',
                minWidth: '140px',
              }}
              onClick={handleStartChange}
            >
              Change Password
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: '1.5rem',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fff',
      }}
    >
      {step === 'initial' && (
        <form onSubmit={handleRequestVerification}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Change Password</h4>
            <InputField
              label="Current Password"
              type="password"
              value={currentPassword}
              setCurrentValue={setCurrentPassword}
              required
              style={{ marginBottom: '1rem' }}
            />

            <InputField
              label="New Password"
              type="password"
              value={newPassword}
              setCurrentValue={setNewPassword}
              required
              style={{ marginBottom: '0.5rem' }}
            />

            {newPassword && (
              <div style={{ marginTop: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div
                    style={{
                      flex: 1,
                      height: '4px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${(passwordStrengthScore / 5) * 100}%`,
                        height: '100%',
                        backgroundColor: getStrengthColor(),
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: getStrengthColor(),
                    }}
                  >
                    {getStrengthLabel()}
                  </span>
                </div>

                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.5rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Password requirements:</div>
                  {passwordRequirements.map((req, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: req.met ? '#28a745' : '#6c757d',
                      }}
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '1rem' }}>
                        {req.met ? 'check_circle' : 'circle'}
                      </span>
                      {req.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <InputField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              setCurrentValue={setConfirmPassword}
              required
              style={{ marginBottom: '0' }}
            />
          </div>

          {error && <p className="form-error">{error}</p>}
          {success && <p className="form-success">{success}</p>}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="submit-button"
              disabled={!currentPassword || !isPasswordValid || !confirmPassword || pendingRequest}
              style={{ minWidth: '140px' }}
            >
              {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Send Verification Code'}
            </button>
            <button
              type="button"
              className="submit-button"
              style={{ backgroundColor: '#6c757d', minWidth: '100px' }}
              onClick={handleCancel}
              disabled={pendingRequest}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {step === 'verify-email' && (
        <form onSubmit={handleVerifyAndChange}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Verify Your Email</h4>
            <div
              style={{
                padding: '1rem',
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '4px',
                marginBottom: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span className="material-icons-outlined" style={{ color: '#0c5460' }}>
                  email
                </span>
                <strong style={{ color: '#0c5460' }}>Verification Code Sent</strong>
              </div>
              <p style={{ margin: 0, color: '#0c5460', fontSize: '0.9rem' }}>
                We've sent a verification code to your email address. Please enter it below to complete the password change.
              </p>
            </div>

            <InputField
              label="Verification Code"
              type="text"
              value={verificationCode}
              setCurrentValue={setVerificationCode}
              required
              placeholder="Enter 6-digit code"
              style={{ marginBottom: '0' }}
            />
          </div>

          {error && <p className="form-error">{error}</p>}
          {success && <p className="form-success">{success}</p>}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="submit-button"
              disabled={!verificationCode || pendingRequest}
              style={{ minWidth: '140px' }}
            >
              {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Verify & Change Password'}
            </button>
            <button
              type="button"
              className="submit-button"
              style={{ backgroundColor: '#6c757d', minWidth: '100px' }}
              onClick={handleCancel}
              disabled={pendingRequest}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default ChangePassword
